import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import {
  cssColorValue,
  cssSpacing,
  ProgressDots,
  semanticColors,
  StatusIndicator,
  TableCell,
  TableRow,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { DeliveryStatus } from '@hooks/types';
import { useLiveInTerritoryAvailability } from '@hooks/useLiveInTerritoryAvailability';
import {
  AlbumSearchResult,
  StoredSearchResult,
} from '@hooks/useSearchedAlbums/types';
import { ExplicitTag } from '@components/shared/styles';
import {
  formatCasingOfReleaseType,
  getMediaType,
  getStatusIndicatorColor,
  isEmptyString,
} from '@helpers/utils';
import { CopyReleaseId } from '../CopyReleaseId';
import { CopyToGreenCheckAnimation } from '../CopyToGreenCheckAnimation';
import { NavigationLink } from './NavigationLink';

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
  height: 1px;
`;

const TerritoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, ${cssSpacing('looser', '32px')});

  [data-live='true'] {
    color: ${cssColorValue(semanticColors.textPositive)};
  }

  [data-live='false'] {
    color: ${cssColorValue(semanticColors.textNegative)};
  }
`;

const StyledTableCell = styled(TableCell)<{ verticalAlignTop?: boolean }>`
  vertical-align: ${({ verticalAlignTop }) =>
    verticalAlignTop ? 'top' : 'middle'};
  height: inherit;
`;

const TypeWithPaddingLeft = styled(Type)`
  padding-left: ${cssSpacing('tighter-2', '16px')};
`;

const StyledNameAndExplicitTagContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ExplicitTagWrapper = styled.span`
  margin-left: -7px;
`;

export const ColumnAnchor = styled.a<{ justifyCenter: boolean }>`
  text-decoration: none;
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: ${props => (props.justifyCenter ? 'center' : 'initial')};
`;

const CopyReleaseIdContainer = styled.div`
  text-decoration: none;
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
`;

const TerritoriesDataContainer = styled.div<{
  renderAsFlexColumn?: boolean;
  justifyCenter?: boolean;
}>`
  align-items: 'flex-start';
  flex-direction: 'column';
  justify-content: 'center';
`;

interface ResultsTableRowProps {
  result: AlbumSearchResult | StoredSearchResult;
  showFeedNameColumn?: boolean;
  showLicensorNameColumn?: boolean;
  showDeliveryStatusColumn: boolean;
  gaEvents: {
    handleTableRowClick: () => void;
    handleClickCopyReleaseId: () => void;
    handleClickCopyURI: () => void;
    handleExpandTerritoriesClick: (prev: boolean) => void;
    handleLiveInTerritoriesApiRetry: () => void;
  };
}

export const ResultsTableRow = ({
  result,
  showFeedNameColumn,
  showLicensorNameColumn,
  showDeliveryStatusColumn,
  gaEvents,
}: ResultsTableRowProps) => {
  const t = useT();
  const {
    name,
    artistName,
    licensorName,
    originalReleaseDate,
    type,
    uri,
    feedName,
    mediaType,
    titleVersion = '',
    hasAtLeastOneExplicitTrack = false,
    primaryReleaseId = '',
    upc,
    ean,
  } = result;
  let deliveryStatus = '';

  if ('deliveryStatus' in result) {
    deliveryStatus = result.deliveryStatus;
  }

  const [rowIsHovered, setRowHovered] = useState(false);
  const uriParts = uri.split(':');
  const albumUri = uriParts[uriParts.length - 1];
  const hasIdentifier =
    !isEmptyString(primaryReleaseId) ||
    !isEmptyString(upc) ||
    !isEmptyString(ean);
  const upcOrEan = [upc, ean].filter(item => !isEmpty(item)).join(', ');

  const [showExpandedRow, toggleShowExpandedRow] = useState(false);

  // items added to release history before primaryReleaseId was added are stored in the user's local history
  // without primaryReleaseId but with upc and ean so we still need to check if those values exist if there's
  // no primaryReleaseId
  let identifier;
  if (isEmptyString(primaryReleaseId) && !isEmptyString(upcOrEan)) {
    identifier = upcOrEan;
  } else {
    identifier = primaryReleaseId;
  }

  // @TO-DO: Refactor to use reusable LiveInTerritories component
  const {
    data: liveInTerritoriesData,
    error,
    mutate,
    isLoading: isLiveInTerritoryAvailabilityLoading,
  } = useLiveInTerritoryAvailability(
    showExpandedRow && typeof albumUri === 'string' ? albumUri : '',
  );

  let liveInTerritoriesArray: [string, boolean][] = [];
  let liveInTerritoriesCount;
  let totalTerritoriesCount;

  if (liveInTerritoriesData && !isEmpty(liveInTerritoriesData)) {
    // Remove if we want to render RU data
    const { RU, ...availabilityWithoutRU } =
      liveInTerritoriesData?.availability;
    // @ts-ignore
    liveInTerritoriesArray = Object.entries(availabilityWithoutRU).sort();

    liveInTerritoriesCount = liveInTerritoriesArray.filter(
      ([, isLive]) => isLive,
    ).length;

    totalTerritoriesCount = liveInTerritoriesArray.length;
  }

  const handleTableRowClick = () => {
    gaEvents.handleTableRowClick();
  };

  const handleExpandTerritoriesClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleShowExpandedRow(prevShowExpandedRow => {
      gaEvents.handleExpandTerritoriesClick(!prevShowExpandedRow);
      return !prevShowExpandedRow;
    });
  };

  const handleLiveInTerritoriesApiRetry = (evt: any) => {
    gaEvents.handleLiveInTerritoriesApiRetry();
    evt.preventDefault();
    evt.stopPropagation();
    mutate();
  };

  const showLiveInTerritoriesData = showExpandedRow && !!liveInTerritoriesData;
  const isLoading = showExpandedRow && isLiveInTerritoryAvailabilityLoading;
  const releaseName = titleVersion?.length ? `${name} (${titleVersion})` : name;

  const renderStatusText = () => {
    if (deliveryStatus === DeliveryStatus.SUCCEEDED) {
      return t(
        'atlas.statuses.ingested',
        'Ingested',
        'Indicates delivery was fully ingested into Spotify',
      );
    }
    if (deliveryStatus === DeliveryStatus.PENDING) {
      return t(
        'atlas.statuses.processing',
        'Processing',
        'Delivery processing',
      );
    }
    if (deliveryStatus === DeliveryStatus.FAILED) {
      return t('atlas.statuses.failed', 'Failed', 'Delivery failed');
    }
    return t('atlas.statuses.unknown', 'Unknown', 'Delivery unknown');
  };

  const COPY_URI = t(
    'atlas.actions.copy_uri',
    `Copy URI`,
    'Text for Copy URI action',
  );

  return (
    <StyledTableRow
      hover
      onClick={handleTableRowClick}
      onMouseEnter={() => setRowHovered(true)}
      onMouseLeave={() => setRowHovered(false)}
    >
      {showFeedNameColumn && (
        <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
          <NavigationLink albumUri={albumUri}>
            <Type
              variant="viola"
              semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
            >
              {feedName}
            </Type>
          </NavigationLink>
        </StyledTableCell>
      )}
      {showLicensorNameColumn && (
        <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
          <NavigationLink albumUri={albumUri}>
            <Type
              variant="viola"
              semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
            >
              {licensorName}
            </Type>
          </NavigationLink>
        </StyledTableCell>
      )}

      <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
        <NavigationLink albumUri={albumUri}>
          <Type variant="viola" weight="bold" semanticColor="textBase">
            <div>
              <Type variant="viola" semanticColor="textBase">
                <StyledNameAndExplicitTagContainer>
                  {releaseName}
                  {hasAtLeastOneExplicitTrack && (
                    <ExplicitTagWrapper>
                      <ExplicitTag
                        aria-label={t(
                          'atlas.labels.explicit_lyrics',
                          'Explicit lyrics',
                          'Label for Explicit lyrics. The value will be a boolean, i.e. "Yes" or "No".',
                        )}
                      />
                    </ExplicitTagWrapper>
                  )}
                </StyledNameAndExplicitTagContainer>
              </Type>
            </div>
            <Type variant="viola" semanticColor="textSubdued">
              {artistName}
            </Type>
          </Type>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
        {hasIdentifier ? (
          <CopyReleaseIdContainer>
            <CopyReleaseId
              primaryReleaseId={identifier}
              gaHandleClickCopyReleaseId={gaEvents.handleClickCopyReleaseId}
            />
          </CopyReleaseIdContainer>
        ) : (
          <NavigationLink albumUri={albumUri}>
            <TypeWithPaddingLeft>-</TypeWithPaddingLeft>
          </NavigationLink>
        )}
      </StyledTableCell>
      <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {formatCasingOfReleaseType(type)}
          </Type>
        </NavigationLink>
      </StyledTableCell>
      <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {getMediaType(mediaType, t)}
          </Type>
        </NavigationLink>
      </StyledTableCell>
      <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {originalReleaseDate}
          </Type>
        </NavigationLink>
      </StyledTableCell>
      {showDeliveryStatusColumn && (
        <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
          <NavigationLink albumUri={albumUri}>
            <StatusIndicator
              semanticColor={getStatusIndicatorColor(deliveryStatus)}
            >
              <Type
                variant="viola"
                semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
              >
                {renderStatusText()}
              </Type>
            </StatusIndicator>
          </NavigationLink>
        </StyledTableCell>
      )}
      <StyledTableCell verticalAlignTop={showLiveInTerritoriesData}>
        {isLoading && (
          <NavigationLink albumUri={albumUri} justifyCenter>
            <ProgressDots size="small" />
          </NavigationLink>
        )}
        {!isLoading && error && (
          <>
            <NavigationLink albumUri={albumUri} renderAsFlexColumn>
              <Type variant="viola" semanticColor="textBase">
                {t(
                  'atlas.errors.error_loading',
                  'Error loading',
                  'Loading error state',
                )}
                <br />
              </Type>
            </NavigationLink>
            <TextLink
              semanticColor="textBrightAccent"
              tabIndex={0}
              standalone
              component="button"
              onClick={handleLiveInTerritoriesApiRetry}
            >
              {t(
                'atlas.actions.try_again',
                'Try again',
                'Text button to try fetch data again',
              )}
            </TextLink>
          </>
        )}
        <TerritoriesDataContainer>
          {showLiveInTerritoriesData && (
            <NavigationLink
              albumUri={albumUri}
              renderAsFlexColumn
              justifyCenter
            >
              <Type
                variant="viola"
                semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
                as="div"
              >
                {liveInTerritoriesCount} / {totalTerritoriesCount}
              </Type>
              <TerritoryGrid>
                {liveInTerritoriesArray.map(([territory, isAvailable]) => {
                  return (
                    <Type
                      variant="viola"
                      data-live={isAvailable}
                      key={territory}
                    >
                      {territory}
                    </Type>
                  );
                })}
              </TerritoryGrid>
            </NavigationLink>
          )}
          {!isLoading && !error && (
            <TextLink
              semanticColor="textBrightAccent"
              tabIndex={0}
              standalone
              component="button"
              onClick={handleExpandTerritoriesClick}
            >
              {showLiveInTerritoriesData
                ? t(
                    'atlas.actions.collapse_view',
                    'Collapse view',
                    'Text button to collapse expanded row',
                  )
                : t(
                    'atlas.actions.expand_view',
                    'Expand to view',
                    'Text button to expand row',
                  )}
            </TextLink>
          )}
        </TerritoriesDataContainer>
      </StyledTableCell>
      <StyledTableCell
        verticalAlignTop={showLiveInTerritoriesData}
        align="center"
      >
        <CopyToGreenCheckAnimation
          label="Copy URI"
          hasHoverBackground
          tooltipText={COPY_URI}
          screenReaderText={COPY_URI}
          copyText={uri}
          snackbarText={t(
            'atlas.snackbars.copied_to_clipboard',
            'Copied to clipboard',
            'Snackbar message to indicate copied to clipboard',
          )}
          handleClick={gaEvents.handleClickCopyURI}
        />
      </StyledTableCell>
    </StyledTableRow>
  );
};
